import { useEffect, useReducer, useCallback } from 'react';
import { getRecentFromCache } from '../utils/cache';
import { fetchKoreanDramas } from './koreanDramas'; // Assuming this accepts an AbortSignal
import { API_KEY, BASE_URL } from './apiConfig'; // Shared constants

const CUSTOM_API_URL = 'https://api-blond-ten.vercel.app/api/scraper/netflix/tv';
const CACHE_KEY_TOP10 = 'netflixTop10PH';

// --- STATE MANAGEMENT (useReducer) ---

const initialState = {
  isLoading: true,
  loadingProgress: 0,
  bannerItem: null,
  movies: [],
  tvShows: [],
  anime: [],
  koreanDramas: [],
  recentlyWatched: [],
};

function movieDataReducer(state, action) {
  switch (action.type) {
    case 'FETCH_START':
      // Reset everything except recentlyWatched on a new fetch cycle
      return { ...initialState, recentlyWatched: state.recentlyWatched, isLoading: true };
    case 'SET_DATA':
      return { ...state, [action.payload.category]: action.payload.data };
    case 'SET_BANNER':
      return { ...state, bannerItem: action.payload };
    case 'UPDATE_PROGRESS':
      return { ...state, loadingProgress: Math.min(action.payload, 100) };
    case 'FETCH_COMPLETE':
      // A small delay can be handled in the component if needed for CSS transitions
      return { ...state, isLoading: false, loadingProgress: 100 };
    case 'FETCH_ERROR':
      console.error("A fetch operation failed:", action.payload.error);
      // Decide how to handle errors, e.g., show an error message.
      // For now, we just stop loading.
      return { ...state, isLoading: false, loadingProgress: 100 };
    default:
      throw new Error(`Unhandled action type: ${action.type}`);
  }
}

function isCacheValid(savedTimestamp) {
  // If there's no timestamp, the cache is definitely not valid.
  if (!savedTimestamp) {
    return false;
  }

  const now = new Date();
  const savedDate = new Date(savedTimestamp);

  const updateHourUTC = 16; // This was 10 for 6 PM, now it's 16 for 12 AM.

  // Create a Date object for today's deadline in UTC.
  const deadlineToday = new Date(now);
  deadlineToday.setUTCHours(updateHourUTC, 0, 0, 0); // Sets time to 16:00:00.000 UTC

  // Determine the most recent deadline that has passed.
  let mostRecentDeadline;
  if (now.getTime() >= deadlineToday.getTime()) {
    // If we are at or after 12 AM PHT today (16:00 UTC), the deadline was today.
    mostRecentDeadline = deadlineToday;
  } else {
    // If it's still before 12 AM PHT today, the last valid deadline was yesterday.
    const deadlineYesterday = new Date(deadlineToday);
    deadlineYesterday.setDate(deadlineToday.getDate() - 1); // Go back one day
    mostRecentDeadline = deadlineYesterday;
  }

  // The cache is valid ONLY if it was saved AFTER the most recent deadline.
  return savedDate.getTime() > mostRecentDeadline.getTime();
}

/**
 * A generic fetch wrapper that includes AbortSignal and basic error handling.
 */
const fetchJSON = async (url, options = {}) => {
  const res = await fetch(url, options);
  if (!res.ok) {
    throw new Error(`HTTP error! status: ${res.status} for URL: ${url}`);
  }
  return res.json();
};


// --- ASYNC DATA FETCHING LOGIC ---

const fetchTrending = async (type, signal) => {
  try {
    const data = await fetchJSON(`${BASE_URL}/trending/${type}/week?api_key=${API_KEY}`, { signal });
    return data.results || [];
  } catch (error) {
    if (error.name === 'AbortError') console.log(`fetchTrending ${type} aborted.`);
    else console.error(`Error fetching trending ${type}:`, error);
    return []; // Return empty array on error
  }
};

const fetchTrendingAnime = async (signal) => {
  try {
    const pagePromises = [1, 2, 3].map(page =>
      fetchJSON(`${BASE_URL}/trending/tv/week?api_key=${API_KEY}&page=${page}`, { signal })
    );
    
    const pages = await Promise.all(pagePromises);
    return pages.flatMap(page =>
      (page?.results || []).filter(item =>
        item?.original_language === 'ja' && item?.genre_ids?.includes(16)
      )
    );
  } catch (error) {
    if (error.name === 'AbortError') console.log('fetchTrendingAnime aborted.');
    else console.error("Error fetching anime:", error);
    return [];
  }
};

const fetchTopNetflixPH = async (signal) => {
  try {
    const cacheItem = JSON.parse(localStorage.getItem(CACHE_KEY_TOP10) || 'null');
    if (cacheItem?.data && isCacheValid(cacheItem.savedAt)) {
      return cacheItem.data;
    }

    const customApiRes = await fetchJSON(CUSTOM_API_URL, { signal });
    if (!customApiRes?.success || !customApiRes.data) {
      throw new Error("Invalid response from custom scraper API");
    }

    const detailPromises = customApiRes.data.map(item =>
      fetchJSON(`${BASE_URL}/tv/${item.tmdb_id}?api_key=${API_KEY}`, { signal })
    );

    const fullData = (await Promise.all(detailPromises))
      .filter(Boolean)
      .map(item => ({ ...item, media_type: 'tv' }));

    localStorage.setItem(CACHE_KEY_TOP10, JSON.stringify({
      data: fullData,
      savedAt: Date.now()
    }));

    return fullData;
  } catch (error) {
    if (error.name === 'AbortError') console.log('fetchTopNetflixPH aborted.');
    else console.error("Error in fetchTopNetflixPH:", error);
    return [];
  }
};


// --- THE CUSTOM HOOK ---

export const useMovieData = () => {
  const [state, dispatch] = useReducer(movieDataReducer, initialState);

  useEffect(() => {
    // AbortController to cancel fetches on component unmount
    const controller = new AbortController();
    const { signal } = controller;

    const loadAllData = async () => {
      dispatch({ type: 'FETCH_START' });

      // Step 1: Load synchronous data from cache
      dispatch({ type: 'SET_DATA', payload: { category: 'recentlyWatched', data: getRecentFromCache() || [] } });
      dispatch({ type: 'UPDATE_PROGRESS', payload: 10 });

      // Step 2: Define all async operations
      const operations = {
        movies: fetchTrending('movie', signal),
        tvShows: fetchTopNetflixPH(signal),
        anime: fetchTrendingAnime(signal),
        koreanDramas: fetchKoreanDramas(signal), // Assuming this is also an async fetcher
      };

      const operationEntries = Object.entries(operations);
      const totalOperations = operationEntries.length;
      let completedOperations = 0;

      // Step 3: Execute all fetches in parallel
      await Promise.all(
        operationEntries.map(async ([category, promise]) => {
          try {
            const data = await promise;
            if (signal.aborted) return; // Don't update state if component has unmounted

            dispatch({ type: 'SET_DATA', payload: { category, data } });

            // Set the banner from the movies list
            if (category === 'movies' && data?.length > 0) {
              const randomItem = data[Math.floor(Math.random() * data.length)];
              dispatch({ type: 'SET_BANNER', payload: randomItem });
            }
          } catch (error) {
             // Errors are already logged in fetch functions, just continue.
          } finally {
            if (!signal.aborted) {
              completedOperations++;
              const progress = 10 + Math.floor((completedOperations / totalOperations) * 90);
              dispatch({ type: 'UPDATE_PROGRESS', payload: progress });
            }
          }
        })
      );
      
      if (!signal.aborted) {
        dispatch({ type: 'FETCH_COMPLETE' });
      }
    };

    loadAllData().catch(error => {
      if (error.name !== 'AbortError') {
        dispatch({ type: 'FETCH_ERROR', payload: { error } });
      }
    });

    // Cleanup function: Abort all in-flight fetches when the component unmounts
    return () => {
      controller.abort();
    };
  }, []); // Empty dependency array ensures this runs only once on mount

  // Expose a stable function to update recently watched items from other components
  const setRecentlyWatched = useCallback((items) => {
    dispatch({ type: 'SET_DATA', payload: { category: 'recentlyWatched', data: items } });
  }, []);

  return { ...state, setRecentlyWatched };
};